//components/pools/LiquidationPool.tsx
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import Decimal from 'decimal.js'
import Image from "next/legacy/image"
import { useEffect, useRef, useState } from 'react'
import { Button, Card, Col, Form, InputGroup, Row, Table } from 'react-bootstrap'
import { connect, useDispatch, useSelector } from 'react-redux'
//@ts-ignore
import hdgCoin from '../../assets/coins/hdg-white.svg'
//@ts-ignore
import ushCoin from '../../assets/coins/ush-white.svg'
import { SignSendConfirmTransaction } from '../../lib/SignSendConfirmTransaction'
import TransactionStep from '../../lib/TransactionStep'
import { numberWithCommas } from '../../lib/Utils'
import { handleConnectionError } from '../../lib/utils/handleConnectionError'
import { LiquidationPosition } from '../../on-chain/accounts/LiquidationPoolPosition'
import { LiquidationPoolState } from '../../on-chain/accounts/LiquidationPoolState'
import ReferralAccount from '../../on-chain/accounts/ReferralAccount'
import {
  fetchCurrentPoolEra,
  fetchLiquidationPoolState,
  fetchUserLiquidationPoolPosition
} from '../../on-chain/loaders/LoadLiquidationPositions'
import {
  getClaimLiquidationPoolPositions,
  getClaimLiquidationPoolPositionsV0,
  getCleanupLiquidationPoolPositions,
  getCleanupLiquidationPoolPositionsV0,
  getCloseLiquidationPoolPositionTransactionStep,
  getDepositLiquidationPoolTransactionStep
} from '../../on-chain/transactions/LiquidationPoolTransactions'
import { getLiquidationPoolStatePublicKey, HedgeProgram } from '../../on-chain/utils/HedgeConstants'
import { FetchReferralAccountForUser } from '../../on-chain/utils/Referral'
import { RootState } from '../../pages/_app'
import { refreshWallet } from '../../reducers/walletReducer'
import InfoHover from '../InfoHover'
import VaultType from "../../on-chain/accounts/VaultType";
import { Collateral } from "../../on-chain/utils/OracleAccounts";
import Balance from "../Balance";
import DropdownBalance, { TokenBalance } from "../DropdownBalance";
import { SignSendConfirmVersionedTransaction } from '../../lib/SignSendConfirmVersionedTransaction'


function LiquidationUshSol() {
  const [totalUshDeposits, setTotalUshDeposits] = useState(new Decimal(0))
  const [depositInput, setDepositInput] = useState('')
  const [openPoolPositions, setOpenPoolPositions] = useState<LiquidationPosition[]>([])
  const [closedPoolPositions, setClosedPoolPositions] = useState<LiquidationPosition[]>([])
  const [liquidationPoolState, setLiquidationPoolState] = useState()
  const [isLoadingPool, setIsLoadingPool] = useState(true)
  const [isLoadingPositions, setIsLoadingPositions] = useState(true)
  const [timeFoo, setTimeFoo] = useState(0)
  const [processingTransaction, setProcessingTransaction] = useState(false)
  const [referralAccount, setReferralAccount] = useState<ReferralAccount>()
  const mountedRef = useRef(false)
  const recentPrices = useSelector(
    (state: RootState) => state.wallet.recentPrices
  ) as Immutable.Map<string, number>
  const networkConnection = useSelector((state: RootState) => state.wallet.networkConnection)
  const userUshBalance = useSelector((state: RootState) => state.wallet.userUshBalance)
  const hdgPrice = useSelector((state: RootState) => state.wallet.hdgPrice)
  const ushPrice = useSelector((state: RootState) => state.wallet.ushPrice)
  const vaultTypes: VaultType[] = useSelector((state: RootState) => state.wallet.vaultTypes)
  const dispatch = useDispatch()

  const { connection } = useConnection()
  const { publicKey: userWalletPublicKey } = useWallet()
  // const userWalletPublicKey = new PublicKey('HKaAxhZ9E1WBB5X5LuEQgoh6DtHRede7y9qDdPtbKiwB')
  const Wallet = useWallet()

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeFoo(Date.now())
    }, 1000 / 40) // 40 FPS Refresh
    return () => clearInterval(interval)
  }, [])

  // effect just for tracking mounted state
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    refreshPoolState()
    refreshPoolPositions()
    setIsLoadingPool(true)
    setIsLoadingPositions(true)
    reloadReferralAccount()
  }, [networkConnection, userWalletPublicKey])

  const refreshPoolPositions = async () => {
    console.log('refreshPoolPositions()')
    if (!userWalletPublicKey) {
      return
    }
    const poolPositionsRaw = await fetchUserLiquidationPoolPosition(
      connection,
      Wallet,
      userWalletPublicKey
    ).catch(handleConnectionError)
    if (!poolPositionsRaw) {
      setIsLoadingPositions(false)
      return
    }

    console.log('poolPositionsRaw: ', poolPositionsRaw)

    const openPoolPositions =
      poolPositionsRaw.filter((position) => {
        return position.open
      }) || []
    setOpenPoolPositions(openPoolPositions)

    const closedPoolPositions =
      poolPositionsRaw.filter((position) => {
        return !position.open
      }) || []
    setClosedPoolPositions(closedPoolPositions)

    setIsLoadingPositions(false)
  }

  const refreshPoolState = async () => {
    console.log('refreshPoolState()')
    setLiquidationPoolState(undefined)
    const liquidationPoolState = await fetchLiquidationPoolState(connection, Wallet).catch(
      handleConnectionError
    )
    setLiquidationPoolState(liquidationPoolState)
    setIsLoadingPool(false)
  }

  function reloadReferralAccount() {
    setReferralAccount(undefined)
    if (userWalletPublicKey) {
      const program = HedgeProgram(connection, Wallet)
      FetchReferralAccountForUser(userWalletPublicKey, program).then((referralAccount) => {
        setReferralAccount(referralAccount)
      })
    }
  }

  useEffect(() => {
    let id = 0
    getLiquidationPoolStatePublicKey().then((liquidationPoolStatePublicKey) => {
      if (id !== -1) {
        id = connection.onAccountChange(liquidationPoolStatePublicKey, () => {
          refreshPoolPositions()
        })
      }
    })

    return () => {
      if (id > 0) {
        connection.removeAccountChangeListener(id)
      }
      id = -1
    }
  }, [networkConnection])

  useEffect(() => {
    refreshPoolInfo()
  }, [networkConnection, userWalletPublicKey])

  const refreshPoolInfo = async () => {
    const currentPoolEra = await fetchCurrentPoolEra(connection, Wallet, userWalletPublicKey).catch(
      handleConnectionError
    )
    if (currentPoolEra) {
      setTotalUshDeposits(currentPoolEra.totalDeposits)
      setIsLoadingPool(false)
    }
  }

  const getParsedDepositInput = (): Decimal => {
    const val = parseFloat(depositInput)
    if (val) {
      return new Decimal(val).mul(LAMPORTS_PER_SOL).floor()
    }
  }

  const handleDepositSubmit = async () => {
    let depositAmount = getParsedDepositInput()
    if (!depositAmount) {
      return
    }

    let transactionSteps: TransactionStep[] = []

    const program = HedgeProgram(connection, Wallet)
    let transactionTitle = 'Stability Pool Transaction'

    if (openPoolPositions.length > 0) {
      // There's a position open that needs to be closed
      depositAmount = depositAmount.add(openPoolPositions[0].getUshAvailable())

      transactionSteps.push(
        await getCloseLiquidationPoolPositionTransactionStep(
          program,
          userWalletPublicKey,
          openPoolPositions[0].publicKey,
          openPoolPositions[0].eraPublicKey
        )
      )

      if (openPoolPositions[0].hasUnclaimedTokens()) {
        transactionSteps.push(
          await getClaimLiquidationPoolPositions(
            program,
            userWalletPublicKey,
            openPoolPositions[0].publicKey,
            openPoolPositions[0].eraPublicKey
          )
        )
      }

      transactionSteps.push(
        await getCleanupLiquidationPoolPositions(
          program,
          userWalletPublicKey,
          openPoolPositions[0].publicKey
        )
      )
    }

    transactionSteps.push(
      await getDepositLiquidationPoolTransactionStep(program, userWalletPublicKey, depositAmount)
    )

    SignSendConfirmTransaction(connection, Wallet, transactionSteps, dispatch, transactionTitle)
      .then((resultSignature) => {
        console.log('Signature Result', resultSignature)
        dispatch(refreshWallet(connection, userWalletPublicKey))
        if (mountedRef.current) {
          refreshPoolPositions()
          refreshPoolState()
          refreshPoolInfo()
          setDepositInput('')
          setProcessingTransaction(false)
        }
      })
      .catch((error) => {
        if (mountedRef.current) {
          setProcessingTransaction(false)
        }
      })
  }

  const handleClaimOnly = async () => {
    if (openPoolPositions.length > 0) {
      let transactionSteps: TransactionStep[] = []

      const program = HedgeProgram(connection, Wallet)
      let transactionTitle = 'Stability Pool Transaction'

      // There's a position open that needs to be closed
      const depositAmount = openPoolPositions[0].getUshAvailable()

      transactionSteps.push(
        await getCloseLiquidationPoolPositionTransactionStep(
          program,
          userWalletPublicKey,
          openPoolPositions[0].publicKey,
          openPoolPositions[0].eraPublicKey
        )
      )

      if (openPoolPositions[0].hasUnclaimedTokens()) {
        transactionSteps.push(
          await getClaimLiquidationPoolPositions(
            program,
            userWalletPublicKey,
            openPoolPositions[0].publicKey,
            openPoolPositions[0].eraPublicKey
          )
        )
      }

      transactionSteps.push(
        await getCleanupLiquidationPoolPositions(
          program,
          userWalletPublicKey,
          openPoolPositions[0].publicKey
        )
      )

      transactionSteps.push(
        await getDepositLiquidationPoolTransactionStep(program, userWalletPublicKey, depositAmount)
      )

      SignSendConfirmTransaction(connection, Wallet, transactionSteps, dispatch, transactionTitle)
        .then((resultSignature) => {
          console.log('Signature Result', resultSignature)
          dispatch(refreshWallet(connection, userWalletPublicKey))
          if (mountedRef.current) {
            refreshPoolPositions()
            refreshPoolState()
            refreshPoolInfo()
            setDepositInput('')
            setProcessingTransaction(false)
          }
        })
        .catch((error) => {
          if (mountedRef.current) {
            setProcessingTransaction(false)
          }
        })
    }
  }

  const handleClickWithdraw = async () => {
    if (openPoolPositions.length > 0) {
      let transactionSteps: TransactionStep[] = []

      const program = HedgeProgram(connection, Wallet)
      let transactionTitle = 'Withdraw Stability Pool Transaction'

      transactionSteps.push(
        await getCloseLiquidationPoolPositionTransactionStep(
          program,
          userWalletPublicKey,
          openPoolPositions[0].publicKey,
          openPoolPositions[0].eraPublicKey
        )
      )

      if (openPoolPositions[0].hasUnclaimedTokens()) {
        transactionSteps.push(
          await getClaimLiquidationPoolPositions(
            program,
            userWalletPublicKey,
            openPoolPositions[0].publicKey,
            openPoolPositions[0].eraPublicKey
          )
        )
      }

      transactionSteps.push(
        await getCleanupLiquidationPoolPositions(
          program,
          userWalletPublicKey,
          openPoolPositions[0].publicKey
        )
      )

      SignSendConfirmTransaction(connection, Wallet, transactionSteps, dispatch, transactionTitle)
        .then((resultSignature) => {
          console.log('Signature Result', resultSignature)
          dispatch(refreshWallet(connection, userWalletPublicKey))
          if (mountedRef.current) {
            refreshPoolPositions()
            refreshPoolState()
            refreshPoolInfo()
            setDepositInput('')
            setProcessingTransaction(false)
          }
        })
        .catch((error) => {
          if (mountedRef.current) {
            setProcessingTransaction(false)
          }
        })
    }
  }

  const handleClickCleanup = async (position: LiquidationPosition) => {
    let transactionSteps: TransactionStep[] = []

    const program = HedgeProgram(connection, Wallet)
    let transactionTitle = 'Cleanup Stability Pool Position'

    if (position.hasUnclaimedTokens()) {
      const claimSteps = await getClaimLiquidationPoolPositionsV0(
        program,
        userWalletPublicKey,
        position.publicKey,
        position.eraPublicKey
      )
      transactionSteps.push(...claimSteps) // Spread the array into individual steps
    }

    transactionSteps.push(
      await getCleanupLiquidationPoolPositionsV0(program, userWalletPublicKey, position.publicKey)
    )

    SignSendConfirmVersionedTransaction(connection, Wallet, transactionSteps, dispatch, transactionTitle)
      .then((resultSignature) => {
        console.log('Signature Result', resultSignature)
        dispatch(refreshWallet(connection, userWalletPublicKey))
        if (mountedRef.current) {
          refreshPoolPositions()
          refreshPoolState()
          refreshPoolInfo()
          setDepositInput('')
          setProcessingTransaction(false)
        }
      })
      .catch((error) => {
        if (mountedRef.current) {
          setProcessingTransaction(false)
        }
      })
  }

  let currentRewardsPerDay = 0
  if (liquidationPoolState) {
    const poolState = new LiquidationPoolState(liquidationPoolState)
    currentRewardsPerDay = poolState.getCurrentHedgeRewardsPerDay()
  }

  let poolAPR = new Decimal(0)
  if (totalUshDeposits.greaterThan(0) && currentRewardsPerDay > 0 && ushPrice) {
    poolAPR = new Decimal(currentRewardsPerDay * 365 * hdgPrice)
      .div(totalUshDeposits.div(LAMPORTS_PER_SOL).mul(ushPrice))
      .mul(100)
  }

  const renderOpenPosition = () => {
    if (openPoolPositions.length === 0) {
      return <div className="p-4 mb-4 text-muted small text-center">NO OPEN POOL POSITION</div>
    }

    let totalUshEntry = 0
    let totalUshExit = 0
    let totalHedge = 0
    let totalSol = 0
    let otherTokens: Map<Collateral, TokenBalance> = new Map<Collateral, TokenBalance>();
    openPoolPositions.forEach((position, _index) => {
      totalUshEntry += position.getInitialDeposit().div(LAMPORTS_PER_SOL).toNumber()
      totalUshExit += position.getUshAvailable().div(LAMPORTS_PER_SOL).toNumber()
      totalHedge += position.getHedgeAvailable().div(LAMPORTS_PER_SOL).toNumber()

      vaultTypes.forEach((vaultType) => {
        if (vaultType.collateral == Collateral.SOL) {
          totalSol += position.getTokensAvailable(vaultType.collateralIndex).div(vaultType.lamportsPerCollateralUnit).toNumber()
        } else {
          let amount = position.getTokensAvailable(vaultType.collateralIndex).div(vaultType.lamportsPerCollateralUnit).toNumber()
          let usdPrice = recentPrices.get(vaultType.collateral)

          if (otherTokens.has(vaultType.collateral)) {
            otherTokens.get(vaultType.collateral).amount += amount
          } else {
            otherTokens.set(vaultType.collateral, {amount, usdPrice})
          }
        }
      })
    })

    if (referralAccount) {
      totalHedge = totalHedge * (1 - referralAccount.stabilityCut.toNumber())
    }

    let totalPositionValue = totalUshExit +
        totalHedge * (hdgPrice ?? 0) +
        totalSol * (recentPrices.get(Collateral.SOL) ?? 0) +
        Array.from(otherTokens.values()).map(token => token.amount * (token.usdPrice ?? 0)).reduce((a, b) => a + b, 0)

    // TODO should be removed?
    // const percentPoolOwnership = new Decimal(totalUshExit).div(totalUshDeposits.div(LAMPORTS_PER_SOL))
    // const valueIn = totalUshEntry
    // let valueOut = 0
    // let apr = 0
    // let percentReturn = 0
    // if (openPoolPositions.length > 0) {
    //   if (recentPrices.get('SOL')) {
    //     const currentPrice: number = recentPrices.get('SOL')
    //     valueOut += totalUshExit * 1 // Current Price of USH
    //     valueOut += totalHedge * hdgPrice // Current Price of HDG
    //     valueOut += totalSol * currentPrice // Current Price of SOL
    //
    //     const profit = valueOut - valueIn
    //     const secondsIn = Date.now() / 1000 - openPoolPositions[0].timestampOpened
    //     percentReturn = profit / valueIn
    //     apr = percentReturn / (secondsIn / (60 * 60 * 24 * 365))
    //     apr = apr * 100
    //   }
    // }

    return (
      <div className="" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
        <Row className="p-4">
          <div className="d-flex justify-content-between">
            <div className="mb-4 d-flex">
              <div className="flex-fill">
                <div className="mb-1 text-muted small">WHAT YOU DEPOSITED</div>
                <div className="d-flex align-items-center mb-2">
                  <Balance collateral="USH" amount={totalUshEntry} />
                </div>
              </div>
            </div>

            <div className="text-end">
              <div className="d-flex align-items-center">
                <div className="flex-fill">
                  <div className="mb-1 text-muted small">WHAT YOUR CURRENT POSITION IS WORTH</div>
                  <div className="mb-2">
                    <Balance collateral="USH" amount={totalUshExit} />
                  </div>
                  <div className="mb-2">
                    {recentPrices.get('SOL') && (
                      <Balance
                        collateral="SOL"
                        amount={totalSol}
                        tooltip={`SOL Price: $${recentPrices.get('SOL').toFixed(2)}`}
                      />
                    )}
                  </div>
                  <div className="mb-2">
                    <DropdownBalance tokenToBalance={otherTokens}/>
                  </div>
                  <div className="mb-2">
                    <Balance
                      collateral="HDG"
                      amount={totalHedge}
                      decimals={6}
                      tooltip={`HDG Price: $${hdgPrice.toFixed(2)}`}
                    />
                  </div>
                  <div className="mb-2 small text-muted fw-bold">
                    Total Position Value:{' '}
                    <span className="text-light">
                      {numberWithCommas(totalPositionValue, 2)}
                    </span>
                  </div>

                  <Button
                    className="me-2"
                    variant="outline-light"
                    onClick={handleClickWithdraw}
                    size="sm"
                    disabled={processingTransaction}
                  >
                    Close {'&'} Claim Position
                  </Button>
                  <Button
                    variant="outline-light"
                    onClick={handleClaimOnly}
                    size="sm"
                    disabled={processingTransaction}
                  >
                    Claim Position
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Row>
      </div>
    )
  }

  const renderClosedPosition = () => {
    if (closedPoolPositions.length === 0) {
      return
    }

    return (
      <Row className="p-4">
        <Col>
          <div className="text-center mb-2">
            You have one or more closed positions that have not been claimed.
          </div>
          <Table variant="dark">
            <tr className="fw-bold">
              <th>Position</th>
              <th>State</th>
              <th></th>
            </tr>
            {closedPoolPositions.map((position, index) => {
              let text = 'Re-claim Rent on'
              if (position.hasUnclaimedTokens()) {
                text = 'Claim Rewards on'
              }
              return (
                <tr key={index}>
                  <td className="small font-monospace">
                    {position.publicKey.toString().substring(0, 6)}...
                    {position.publicKey.toString().substring(38)}
                  </td>
                  <td className="">
                    {position.hasUnclaimedTokens() ? 'Unclaimed Tokens & Rent' : 'Unclaimed Rent'}
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-light"
                      onClick={() => {
                        handleClickCleanup(position)
                      }}
                      size="sm"
                      disabled={processingTransaction}
                      className=""
                    >
                      Claim
                    </Button>
                  </td>
                </tr>
              )
            })}
          </Table>
        </Col>
      </Row>
    )
  }

  const handleClickMaxDeposit = () => {
    setDepositInput((userUshBalance / LAMPORTS_PER_SOL).toString())
  }

  const renderDeposit = () => {
    let depositDisabled = isNaN(parseFloat(depositInput)) || parseFloat(depositInput) <= 0
    let depositButtonText = openPoolPositions.length === 0 ? 'Deposit' : 'Claim & Deposit'

    if (parseFloat(depositInput) > userUshBalance / LAMPORTS_PER_SOL) {
      depositDisabled = true
      depositButtonText = 'Not enough USH'
    }

    if (!isNaN(parseFloat(depositInput)) && parseFloat(depositInput) < 0) {
      depositDisabled = true
      depositButtonText = 'No Negative'
    }

    if (processingTransaction) {
      depositDisabled = true
      depositButtonText = 'Processing...'
    }

    return (
      <div className="mt-1 d-flex align-items-center">
        <div className="flex-fill">
          <Form>
            <Form.Group className="">
              <InputGroup>
                <InputGroup.Text className="py-2">USH</InputGroup.Text>
                <Form.Control
                  value={depositInput}
                  onChange={(ev) => setDepositInput(ev.target.value)}
                  className="py-2 px-3"
                  type="number"
                  placeholder="0"
                  disabled={processingTransaction}
                />
                <Button
                  className="py-2 px-3"
                  variant="light"
                  onClick={handleClickMaxDeposit}
                  disabled={processingTransaction}
                >
                  Max
                </Button>
              </InputGroup>
            </Form.Group>
          </Form>
        </div>
        <div className="ms-3">
          <Button variant="primary" onClick={handleDepositSubmit} disabled={depositDisabled}>
            {depositButtonText}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Card className="mb-4 p-0">
        <Card.Body className="p-0">
          <div className="d-flex flex-column justify-content-between">
            <div>
              <Row className="p-4">
                <Col>
                  <div className="d-flex justify-content-between mb-3">
                    <div className="fw-bold h4">
                      <div>Stability Pool</div>

                      <div className="small text-primary d-flex align-items-center">
                        Buy out liquidated vaults
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="text-muted">
                        Current Pool APR{' '}
                        <InfoHover text={'APR is calculated as current HDG rewards times HDG price divided by total pool deposits. Potential earnings from liquidations are not included'} /> 
                      </div>
                      {poolAPR.isZero() ? (
                        <span>Loading</span>
                      ) : (
                        <span className="mt-2">
                          <span className="text-number h3 fw-bold mb-0">
                            {numberWithCommas(poolAPR, 2)}
                          </span>
                          <span className="ms-1 fw-light h5">%</span>
                        </span>
                      )}{' '}
                    </div>
                  </div>
                  <div>
                    <i className="fa-thin fa-circle-1 me-2"></i>{' '}
                    <span className="text-muted">Deposit USH</span>
                  </div>
                  <div>
                    <i className="fa-thin fa-circle-2 me-2"></i>{' '}
                    <span className="text-muted">
                      Your USH will be used to buy out liquidated vaults at a market discount
                    </span>
                  </div>
                  <div>
                    <i className="fa-thin fa-circle-3 me-2"></i>{' '}
                    <span className="text-muted">Collect HDG tokens as a reward.</span>
                  </div>
                </Col>
              </Row>
              <Row className="p-4 pt-0">
                <Col>
                  <div className="mt-1 mb-1 gradient-box p-4">
                    <div className="d-flex align-items-center text-muted">
                      <Image src={ushCoin} width={28} height={28} alt="USH Coin" />{' '}
                      <span className="mx-2">Total USH in Pool</span>{' '}
                      <InfoHover text="This is the current liquidation pool USH supply. These funds will be used to buy under collateralized vaults at a discount." />
                    </div>
                    <div>
                      {isLoadingPool ? (
                        <div>Loading</div>
                      ) : (
                        <div className="mt-2 d-flex justify-content-start align-items-baseline">
                          <span className="text-number h3 fw-bold mb-0">
                            {numberWithCommas(totalUshDeposits.div(LAMPORTS_PER_SOL), 2)}
                          </span>
                          <span className="mx-2 fw-light">USH</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="mt-1 mb-1 gradient-box p-4">
                    <div className="d-flex align-items-center text-muted">
                      <Image src={hdgCoin} width={28} height={28} alt="HDG Coin" />
                      <span className="mx-2">TOKENS REWARD / DAY</span>{' '}
                      <InfoHover text="This is the instantaneous reward rate for the pool. This rate drops by half each year." />{' '}
                    </div>
                    <div>
                      {currentRewardsPerDay === 0 ? (
                        <div>Loading</div>
                      ) : (
                        <>
                          <div className="mt-2 d-flex justify-content-start align-items-baseline">
                            <span className="text-number h3 fw-bold mb-0">
                              {numberWithCommas(currentRewardsPerDay, 2)}
                            </span>
                            <span className="mx-2 fw-light">HDG</span>
                          </div>
                        </>
                      )}{' '}
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  {userWalletPublicKey || hdgPrice === 0 ? (
                    isLoadingPositions ? (
                      <div className="p-4 text-center">Loading Position</div>
                    ) : (
                      <>
                        <>{renderOpenPosition()}</>
                        <>{renderClosedPosition()}</>
                      </>
                    )
                  ) : (
                    <div className="p-4 mb-4 text-center">No Wallet Connected</div>
                  )}
                </Col>
              </Row>
            </div>
            <div>
              <Row className="p-4">
                <Col>
                  {userWalletPublicKey || hdgPrice === 0 ? (
                    isLoadingPositions ? (
                      <span></span>
                    ) : (
                      <>{renderDeposit()}</>
                    )
                  ) : (
                    ''
                  )}
                </Col>
              </Row>
            </div>
          </div>
        </Card.Body>
      </Card>
    </>
  )
}

function mapStateToProps(state) {
  const { networkConnection, usdSolPrice, userSolBalance, userUshBalance, vaultTypes } = state.wallet
  return {
    networkConnection,
    usdSolPrice,
    userSolBalance,
    userUshBalance,
    vaultTypes,
  }
}
export default connect(mapStateToProps)(LiquidationUshSol)
